import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PickupLocation } from "../../../../api/pickupLocations";
import PickupMap from "../../components/PickupMap";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useClientSubscription } from "../../hooks/useClientSubscription";
import NotInSeasonPage from "../../pages/NotInSeasonPage";
import { ModalContext } from "../../store/ModalContext";
import { PickupLocationContext } from "../../store/PickupLocationContext";
import { PickupContext } from "../../store/PickupContext";
import { SeasonContext } from "../../store/SeasonContext";
import { SubscriptionsContext } from "../../store/SubscriptionsContext";
import { UserPickupContext } from "../../store/UserPickupContext";
import { type ClientPickupFormData, type MapLocation } from "../../utils/types";
import InfoNavPanel from "../../components/InfoNavPanel";

import {
  checkIfThereAreChanges,
  clientPickupFormDataToUpdatePayload,
  pickupInfoPages,
  isFriendPickupInfoComplete,
  shouldButtonBeDisabled,
} from "./ClientDashboard.helpers";
import PickupEditForm from "./PickupEditForm";
import "./ClientDashboard.css";
import LoadingSpinner from "../../../LoadingSpinner";
import FloatingMenu from "../../components/menu/FloatingMenu";
import { isPickupLocationLockedAtSignup } from "../../../../constants/csaPickup";
import { useStartCsaCheckout } from "../../hooks/useStartCsaCheckout";
import {
  ClientWinterBreakMessage,
  ClientCommingSoonMessage,
} from "../../../../utils/dashboard.helpers";

function PayNowButton() {
  const { startCsaCheckout, isStartingCheckout, checkoutError } =
    useStartCsaCheckout();

  return (
    <div className="pay-now-container">
      {checkoutError && <p role="alert">{checkoutError}</p>}
      <button
        type="button"
        className="btn pay-now-btn"
        disabled={isStartingCheckout}
        onClick={() => void startCsaCheckout()}
      >
        {isStartingCheckout ? "Starting payment…" : "Pay now"}
      </button>
    </div>
  );
}
const ClientDashboard = () => {
  const [marker, setMarker] = useState<MapLocation[]>([]);
  const [isChangeDetailsOpen, setIsChangeDetailsOpen] = useState(false);
  const [savedLocation, setSavedLocation] = useState<PickupLocation>();
  const [areThereUnsavedChanges, setAreThereUnsavedChanges] = useState(false);
  const { details } = useAuthUser();
  const userFullName =
    details != null ? `${details.firstName} ${details.lastName}`.trim() : "";
  const { pickupLocations, loading: arePickupLocationsLoading } = useContext(
    PickupLocationContext,
  );
  const { loading: arePickupsLoading } = useContext(PickupContext);
  const { loading: areSubscriptionsLoading, tiers } = useContext(
    SubscriptionsContext,
  );
  const {
    pickup: currentClientPickup,
    updatePickup,
    loading: isUserPickupLoading,
  } = useContext(UserPickupContext);
  const { openModal, closeModal } = useContext(ModalContext);
  const { areWeInSeason, loading: isSeasonLoading } = useContext(SeasonContext);

  const subscription = useClientSubscription();

  const isSubscriptionTierResolved =
    !currentClientPickup ||
    tiers.some((tier) => tier.id === currentClientPickup.subscriptionTierId);

  const isPageLoading =
    isSeasonLoading ||
    arePickupLocationsLoading ||
    isUserPickupLoading ||
    arePickupsLoading ||
    areSubscriptionsLoading ||
    !isSubscriptionTierResolved;

  const isPickupLocationLocked = currentClientPickup
    ? isPickupLocationLockedAtSignup(currentClientPickup.signupPickupLocationId)
    : false;

  const [clientPickupFormData, setClientPickupFormData] =
    useState<ClientPickupFormData>({
      selectedPickupLocation: undefined,
      selectedDay: "",
      friendPickupChecked: false,
      friendFirstName: null,
      friendLastName: null,
      friendPhone: null,
    });

  function getCurrentPickupTimeSlot() {
    const currentPickupLocation = pickupLocations.find(
      (location) => location._id === currentClientPickup?.pickupLocationId,
    );
    const currentDayOption = currentPickupLocation?.days.find(
      (day) => day.day === currentClientPickup?.pickupDay,
    );
    return `${currentDayOption?.startTime} - ${currentDayOption?.endTime}`;
  }
  const locationToMark = pickupLocations.find((location) => {
    return location._id === clientPickupFormData.selectedPickupLocation?._id;
  });

  function handleSaveChanges() {
    const {
      friendPickupChecked,
      friendFirstName,
      friendLastName,
      friendPhone,
    } = clientPickupFormData;

    if (friendPickupChecked) {
      if (!friendFirstName || !friendLastName || !friendPhone) {
        alert(
          "Please enter name and phone number for the person who will pick up your harvest.",
        );
        return;
      }

      if (!currentClientPickup) return;
      updatePickup(
        currentClientPickup.id,
        clientPickupFormDataToUpdatePayload(clientPickupFormData, {
          isPickupLocationLocked,
        }),
      );
      setAreThereUnsavedChanges(false);
      setIsChangeDetailsOpen(false);
      return;
    }

    const clientPickupFormDataWithNoFriendPickup = {
      ...clientPickupFormData,
      friendFirstName: null,
      friendLastName: null,
      friendPhone: null,
    };

    if (!currentClientPickup) return;
    updatePickup(
      currentClientPickup.id,
      clientPickupFormDataToUpdatePayload(
        clientPickupFormDataWithNoFriendPickup,
        {
          isPickupLocationLocked,
        },
      ),
    );

    setAreThereUnsavedChanges(false);
  }

  function handleShowInfo() {
    openModal({
      body: (
        <InfoNavPanel
          infoPages={pickupInfoPages}
          lastPageAction={{ text: "Close", onClick: closeModal }}
        />
      ),
    });
  }

  function handleCancel() {
    if (currentClientPickup) {
      const userPickupLocation = pickupLocations.find((location) => {
        return location._id === currentClientPickup.pickupLocationId;
      });

      setSavedLocation(userPickupLocation);
      setClientPickupFormData((prev) => {
        return {
          ...prev,
          selectedPickupLocation: userPickupLocation,
          selectedDay: currentClientPickup.pickupDay,
          friendPickupChecked: currentClientPickup.friendPickupFirstName
            ? true
            : false,
        };
      });
    }

    setAreThereUnsavedChanges(false);
    setIsChangeDetailsOpen(false);
  }

  const initialUserPickupFetch = useCallback(() => {
    if (currentClientPickup) {
      const userPickupLocation = pickupLocations.find((location) => {
        return location._id === currentClientPickup.pickupLocationId;
      });

      setSavedLocation(userPickupLocation);

      // NEW
      const selectedPickupLocation = userPickupLocation;
      const selectedDay = currentClientPickup.pickupDay;
      const friendPickupChecked = isFriendPickupInfoComplete(
        currentClientPickup,
      )
        ? true
        : false;
      const friendFirstName = isFriendPickupInfoComplete(currentClientPickup)
        ? currentClientPickup.friendPickupFirstName
        : null;
      const friendLastName = isFriendPickupInfoComplete(currentClientPickup)
        ? currentClientPickup.friendPickupLastName
        : null;
      const friendPhone = isFriendPickupInfoComplete(currentClientPickup)
        ? currentClientPickup.friendPickupPhone
        : null;

      setClientPickupFormData((prev) => {
        return {
          ...prev,
          selectedPickupLocation,
          selectedDay,
          friendPickupChecked,
          friendFirstName,
          friendLastName,
          friendPhone,
        };
      });
    }
  }, [pickupLocations, currentClientPickup]);

  const isDataLoaded = useMemo(() => {
    return (
      !arePickupLocationsLoading &&
      pickupLocations.length > 0 &&
      !isUserPickupLoading
    );
  }, [arePickupLocationsLoading, isUserPickupLoading, pickupLocations.length]);

  useEffect(() => {
    if (isDataLoaded) {
      initialUserPickupFetch();
    }
  }, [isDataLoaded, initialUserPickupFetch]);

  useEffect(() => {
    if (isDataLoaded) {
      const areThereChanges = checkIfThereAreChanges(
        currentClientPickup,
        clientPickupFormData,
        isPickupLocationLocked,
      );

      setAreThereUnsavedChanges(!!areThereChanges);
    }
  }, [
    clientPickupFormData,
    currentClientPickup,
    isDataLoaded,
    isPickupLocationLocked,
  ]);

  useEffect(() => {
    if (locationToMark) {
      setMarker([
        {
          address: locationToMark.address,
          latitude: locationToMark.latitude,
          longitude: locationToMark.longitude,
        },
      ]);
    } else {
      setMarker([]);
    }
  }, [locationToMark]);

  if (isPageLoading) {
    return (
      <div className="page-container">
        <LoadingSpinner className="loading-spinner-container--fullscreen" />
        <FloatingMenu
          logoutButton={true}
          homeButton={true}
          isBusiness={false}
        />
      </div>
    );
  }

  if (!areWeInSeason) {
    return <NotInSeasonPage message={ClientWinterBreakMessage} />;
  }
  if (!subscription.isSubscriptionActive) {
    return <NotInSeasonPage message={ClientCommingSoonMessage} />;
  }

  if (!currentClientPickup) {
    return (
      <div className="page-container">
        <h1 className="welcome client-dashboard-h1">
          Welcome {details?.firstName}
        </h1>
        <div className="page-content">
          <div className="info-card client-dashboard-info-card">
            <p>You do not have an active CSA membership.</p>
            <p>
              <Link to="/csa-signup">Go to CSA signup</Link> to get your
              subscription box or request one for next year.
            </p>
          </div>
        </div>
        <FloatingMenu
          logoutButton={true}
          homeButton={true}
          isBusiness={false}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="welcome client-dashboard-h1">
        Welcome {details?.firstName}
      </h1>
      <button className="btn client-dashboard-btn" onClick={handleShowInfo}>
        I need help
      </button>
      <div className="page-content">
        <div>
          {subscription.isSubscriptionActive ? (
            <p>
              Your next harvest is ready for pickup on{" "}
              <span className="bold-capitalized">
                {currentClientPickup?.pickupDay}
              </span>{" "}
              between <span className="bold">{getCurrentPickupTimeSlot()}</span>{" "}
              at <span className="bold-capitalized">{savedLocation?.name}</span>{" "}
              by{" "}
              <span className="bold-capitalized">
                {" "}
                {currentClientPickup?.friendPickupFirstName
                  ? currentClientPickup?.friendPickupFirstName +
                    " " +
                    currentClientPickup.friendPickupLastName
                  : userFullName}
              </span>
            </p>
          ) : (
            <p>
              Deliveries have not yet started for your{" "}
              <span className="bold-capitalized">
                {subscription.subscriptionName ?? "subscription"}
              </span>
              . We will let you know when your first pickup is ready.
            </p>
          )}
          <p className="client-dashboard-above-map">
            Subscription Type:{" "}
            <span className="bold-capitalized">
              {subscription.subscriptionName}
            </span>{" "}
            <br />
            Payment status:{" "}
            <span className="bold-capitalized">
              {currentClientPickup.subscriptionPaymentStatus}
            </span>
          </p>
          {currentClientPickup.subscriptionPaymentStatus === "pending" ||
          currentClientPickup.subscriptionPaymentStatus === "failed" ||
          currentClientPickup.subscriptionPaymentStatus === "expired" ? (
            <PayNowButton />
          ) : null}
        </div>
        <div>
          <PickupMap locations={marker} />
        </div>
        <div className="other-pickup-arrangement">
          {!isChangeDetailsOpen ? (
            <div>
              <button
                className={
                  shouldButtonBeDisabled(pickupLocations, currentClientPickup)
                    ? "btn client-dashboard-btn disabled-btn"
                    : "btn client-dashboard-btn"
                }
                onClick={() => setIsChangeDetailsOpen(true)}
                disabled={shouldButtonBeDisabled(
                  pickupLocations,
                  currentClientPickup,
                )}
              >
                Change My Pickup Details
              </button>
              {shouldButtonBeDisabled(pickupLocations, currentClientPickup) && (
                <div className="btn info-btn info-wrapper">
                  <FontAwesomeIcon icon={faInfo} style={{ color: "#ffffff" }} />
                  <p className="tooltip">
                    Details cannot be changed the day of <br />
                    your chosen delivery. Please try again after <br />
                    your pickup today!
                  </p>
                </div>
              )}
            </div>
          ) : (
            <PickupEditForm
              formData={clientPickupFormData}
              isPickupLocationLocked={isPickupLocationLocked}
              onFormDataChange={(changedFormData: ClientPickupFormData) => {
                setClientPickupFormData(changedFormData);
              }}
            />
          )}
          <div className="buttons-footer-container">
            {areThereUnsavedChanges || isChangeDetailsOpen ? (
              <div className="buttons-footer">
                <button className="btn equal-btns" onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  className={
                    !areThereUnsavedChanges
                      ? "btn disabled-btn equal-btns"
                      : "btn equal-btns "
                  }
                  onClick={handleSaveChanges}
                >
                  Save
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <FloatingMenu logoutButton={true} homeButton={true} isBusiness={false} />
    </div>
  );
};
export default ClientDashboard;
